import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ModelSection } from '../types/ModelSection';
import IModelData from '../types/ModelData';
import apiService from '../services/ApiService';

// Import des composants Field
import StringField from './ModelDataFields/Input/String';
import FloatField from './ModelDataFields/Input/Float';
import IntField from './ModelDataFields/Input/Int';
import DateField from './ModelDataFields/Input/Date';
import EnumField from './ModelDataFields/Input/Enum';
import BoolField from './ModelDataFields/Input/Bool';
import ListField from './ModelDataFields/Input/List';
import TimeSerieField from './ModelDataFields/Input/TimeSerie';
import TimeSerieTimingField from './ModelDataFields/Input/TimeSerieTiming';
import RelationField from './ModelDataFields/Input/Relation';
import TimingReferenceField from './ModelDataFields/Input/TimingReference';
import CustomizableTimingReferenceField from './ModelDataFields/Input/CustomizableTimingReference';

// Import des composants Sensitivity Field
import SensitivityFloatField from './ModelDataFields/Sensitivities/Float';
import SensitivityIntField from './ModelDataFields/Sensitivities/Int';
import SensitivityStringField from './ModelDataFields/Sensitivities/String';
import SensitivityDateField from './ModelDataFields/Sensitivities/Date';
import SensitivityBoolField from './ModelDataFields/Sensitivities/Bool';
import SensitivityEnumField from './ModelDataFields/Sensitivities/Enum';
import SensitivityListField from './ModelDataFields/Sensitivities/List';
import SensitivityTimeSerieField from './ModelDataFields/Sensitivities/TimeSerie';
import SensitivityTimeSerieTimingField from './ModelDataFields/Sensitivities/TimeSerieTiming';
import SensitivityRelationField from './ModelDataFields/Sensitivities/Relation';
import SensitivityTimingReferenceField from './ModelDataFields/Sensitivities/TimingReference';
import SensitivityCustomizableTimingReferenceField from './ModelDataFields/Sensitivities/CustomizableTimingReference';

import snakeToPascal from '../tools';
import { TailwindButton } from './Tailwind';
import TailwindModal from './Tailwind/TailwindModal';
import { cn } from '../lib/utils';
import { removeElemFromTree } from './Draggables/utils/treeUtils';
import { useScenarioTree } from '../shared/hooks/queries/useScenarioTree';

const FieldByType = {
  'str': StringField,
  'int': IntField,
  'float': FloatField,
  'datetime': DateField,
  'enum': EnumField,
  'bool': BoolField,
  'list': ListField,
  'time_serie_timing': TimeSerieTimingField,
  'time_serie': TimeSerieField,
  'relation': RelationField,
  'timing_reference': TimingReferenceField,
  'customizable_timing_reference': CustomizableTimingReferenceField,
};

const SensitivityFieldByType = {
  'str': SensitivityStringField,
  'int': SensitivityIntField,
  'float': SensitivityFloatField,
  'datetime': SensitivityDateField,
  'enum': SensitivityEnumField,
  'bool': SensitivityBoolField,
  'list': SensitivityListField,
  'time_serie_timing': SensitivityTimeSerieTimingField,
  'time_serie': SensitivityTimeSerieField,
  'relation': SensitivityRelationField,
  'timing_reference': SensitivityTimingReferenceField,
  'customizable_timing_reference': SensitivityCustomizableTimingReferenceField,
};

export interface StructureChartEditorProps {
  section: ModelSection;
  onBack: () => void;
  currentState: any;
  currentSensitivity: any;
  scenario_id: string;
  userRights: any;
  reloadState?: () => void;
}

interface CompanyBlock {
  id: string;
  modelDataId: number;
  modelData: IModelData;
  name: string;
  type: string;
  x: number;
  y: number;
  level: number; // Niveau hiérarchique (0 = root)
}

interface Connection {
  id: string;
  fromId: string;
  toId: string;
  ownership?: number; // Pourcentage de ownership si disponible
}

export default function StructureChartEditor({
  section,
  onBack,
  currentState,
  currentSensitivity,
  scenario_id,
  userRights,
  reloadState,
}: StructureChartEditorProps) {
  const [companies, setCompanies] = useState<CompanyBlock[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(0.85);
  const [showOwnershipPercents, setShowOwnershipPercents] = useState(true);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [isExpandedOpen, setIsExpandedOpen] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const workspaceRef = useRef<HTMLDivElement>(null);
  const pendingSelectionRef = useRef<string | null>(null);
  const deletedIdsRef = useRef<Set<number>>(new Set());
  const hasAutoCenteredRef = useRef(false);

  // TanStack tree cache patcher — used by handleDeleteCompany so that the
  // deletion is reflected in the cached tree (and therefore in section.inputItems
  // after the component remounts), not only in local React state.
  const { patchTree } = useScenarioTree(parseInt(scenario_id, 10));

  // Récupérer les model datas de la section structure
  const structureInputs = section.inputItems;

  // Mode sensitivity
  const isSensitivityMode = currentSensitivity && currentSensitivity !== 'null';

  // Helper: récupérer le tree item (avec sensitivityModelData) pour un modelDataId
  const getTreeItem = useCallback((modelDataId: number) => {
    return currentState?.Input?.tree?.items?.[modelDataId.toString()] || null;
  }, [currentState]);

  // Plans can cap the number of companies (Guest Trial: one). The root's children
  // definition says whether one more is allowed, as it does for the "+" menus.
  const companyCreationLocked = useMemo(() => {
    const tree = currentState?.Input?.tree;
    const definitions = tree?.items?.[tree?.rootId]?.childrensDefinition;
    if (!Array.isArray(definitions)) return false;
    const company = definitions.find((def: any) => def?.type === 'company');
    return !company?.instantiable;
  }, [currentState]);

  // Helper: vérifier si un company a des champs modifiés en sensitivity
  const hasSensitivityModified = useCallback((modelDataId: number) => {
    if (!isSensitivityMode) return false;
    const treeItem = getTreeItem(modelDataId);
    return treeItem?.sensitivityModelData != null && Object.keys(treeItem.sensitivityModelData.fields || {}).length > 0;
  }, [isSensitivityMode, getTreeItem]);

  // Récupérer la company sélectionnée
  const selectedCompany = companies.find((c) => c.id === selectedCompanyId);
  const selectedModelData = selectedCompany?.modelData;
  const selectedTreeItem = selectedCompany ? getTreeItem(selectedCompany.modelDataId) : null;

  // Connexions dérivées directement (useMemo au lieu de useState+useEffect = 1 render en moins)
  const connections = useMemo(() => {
    const newConnections: Connection[] = [];
    const inputsByIdMap = new Map(structureInputs.map((md) => [md.id, md]));
    const companyByModelDataIdMap = new Map(companies.map((c) => [c.modelDataId, c]));

    companies.forEach((ownerCompany) => {
      const updatedMd = inputsByIdMap.get(ownerCompany.modelDataId);
      const ownerMd = updatedMd || ownerCompany.modelData;

      if (ownerMd.fields && ownerMd.fields.company_ownership) {
        const ownershipField = ownerMd.fields.company_ownership;

        if (ownershipField.currentValue && Array.isArray(ownershipField.currentValue.value)) {
          ownershipField.currentValue.value.forEach((elem: any, index: number) => {
            if (elem.company && elem.company.value) {
              const ownedCompanyId = parseInt(elem.company.value);
              const ownedCompany = companyByModelDataIdMap.get(ownedCompanyId);

              if (ownedCompany) {
                const percent = elem.percent?.value || null;

                newConnections.push({
                  id: `conn-${ownerCompany.id}-${ownedCompany.id}-${index}`,
                  fromId: ownerCompany.id,
                  toId: ownedCompany.id,
                  ownership: percent ? parseFloat(percent) : undefined,
                });
              }
            }
          });
        }
      }
    });

    return newConnections;
  }, [companies, structureInputs]);

  // Dériver les positions finales : les companies isolées (pas dans les connexions) apparaissent en dessous
  const displayCompanies = useMemo(() => {
    if (companies.length === 0 || connections.length === 0) return companies;

    // Trouver les companies qui participent à au moins une connexion
    const connectedIds = new Set<string>();
    connections.forEach((conn) => {
      connectedIds.add(conn.fromId);
      connectedIds.add(conn.toId);
    });

    const connected = companies.filter((c) => connectedIds.has(c.id));
    const isolated = companies.filter((c) => !connectedIds.has(c.id));

    if (isolated.length === 0) return companies;

    // Calculer la position Y max des companies connectées
    const maxConnectedY = Math.max(...connected.map((c) => c.y));
    const HORIZONTAL_SPACING = 250;
    const isolatedStartY = maxConnectedY + 220;
    const totalWidth = (isolated.length - 1) * HORIZONTAL_SPACING;
    const startX = -totalWidth / 2 + 400;

    return companies.map((c) => {
      if (!connectedIds.has(c.id)) {
        const idx = isolated.findIndex((iso) => iso.id === c.id);
        return { ...c, x: startX + idx * HORIZONTAL_SPACING, y: isolatedStartY };
      }
      return c;
    });
  }, [companies, connections]);

  // Drapeaux pays dérivés du field contenant "country" de chaque company
  const companyFlags = useMemo(() => {
    const inputsMap = new Map(structureInputs.map((md) => [md.id, md]));
    const flags = new Map<string, string>();

    const codeToFlag = (code: string): string => {
      const upper = code.toUpperCase();
      return String.fromCodePoint(
        ...([...upper].map((c) => 0x1F1E6 + c.charCodeAt(0) - 65))
      );
    };

    companies.forEach((company) => {
      const md = inputsMap.get(company.modelDataId) || company.modelData;
      if (!md?.fields) return;

      // Chercher un field dont le nom contient "country"
      const countryFieldKey = Object.keys(md.fields).find((k) =>
        k.toLowerCase().includes('country')
      );
      if (!countryFieldKey) return;

      const field = md.fields[countryFieldKey];
      const value = field.currentValue?.value || field.value?.value;
      if (!value || typeof value !== 'string') return;

      // Accepter les codes ISO 2 lettres (ex: "FR", "fr", "US")
      if (value.length === 2 && /^[a-zA-Z]{2}$/.test(value)) {
        flags.set(company.id, codeToFlag(value));
      }
    });

    return flags;
  }, [companies, structureInputs]);

  // Charger les companies existantes au montage ou quand les données parent changent
  useEffect(() => {
    loadExistingCompanies();

    // Appliquer la sélection en attente (après ajout d'une company)
    if (pendingSelectionRef.current) {
      const newId = pendingSelectionRef.current;
      pendingSelectionRef.current = null;
      setSelectedCompanyId(newId);
      setEditingNameId(newId);
    }
  }, [section.inputItems]);


  const loadExistingCompanies = () => {
    // Filtrer les model datas de type "company" dans les inputs (exclure les supprimés récemment)
    const companyModelDatas = structureInputs.filter((md) => {
      if (deletedIdsRef.current.has(md.id)) {
        // Vérifier si l'API a bien supprimé : si l'item est encore dans structureInputs, on le filtre
        // Une fois que structureInputs ne le contient plus, on nettoie le ref
        return false;
      }
      return md.kind === 'company' || md.type === 'company';
    });

    // Nettoyer les IDs supprimés qui ne sont plus dans structureInputs
    const currentIds = new Set(structureInputs.map((md) => md.id));
    deletedIdsRef.current.forEach((id) => {
      if (!currentIds.has(id)) {
        deletedIdsRef.current.delete(id);
      }
    });

    // Première passe : créer les blocs
    const allCompanies: CompanyBlock[] = companyModelDatas.map((md) => ({
      id: `company-${md.id}`,
      modelDataId: md.id,
      modelData: md,
      name: md.name,
      type: 'Company',
      x: 0,
      y: 0,
      level: 0,
    }));

    // Deuxième passe : calculer les niveaux hiérarchiques et positionner
    const positionedCompanies = calculateHierarchyAndPosition(allCompanies, companyModelDatas);

    setCompanies(positionedCompanies);
  };

  const calculateHierarchyAndPosition = (allCompanies: CompanyBlock[], companyModelDatas: IModelData[]) => {
    // Créer un mapping id -> company
    const companyMap = new Map(allCompanies.map((c) => [c.modelDataId, c]));

    // Identifier toutes les références (qui possède qui)
    const ownershipRefs = new Map<number, number[]>(); // ownerId -> [ownedCompanyIds]

    companyModelDatas.forEach((md) => {
      if (md.fields && md.fields.company_ownership) {
        const ownershipField = md.fields.company_ownership;

        if (ownershipField.currentValue && Array.isArray(ownershipField.currentValue.value)) {
          const ownedCompanies: number[] = [];

          ownershipField.currentValue.value.forEach((elem: any) => {
            // Chaque élément de la liste a une structure comme { company: { value: id }, ... }
            if (elem.company && elem.company.value) {
              ownedCompanies.push(parseInt(elem.company.value));
            }
          });

          if (ownedCompanies.length > 0) {
            ownershipRefs.set(md.id, ownedCompanies);
          }
        }
      }
    });

    // Identifier les companies possédées (enfants)
    const ownedCompanyIds = new Set<number>();
    ownershipRefs.forEach((ownedIds) => {
      ownedIds.forEach((id) => ownedCompanyIds.add(id));
    });

    // Identifier les companies root (non possédées par une autre)
    const rootCompanyIds = allCompanies
      .map((c) => c.modelDataId)
      .filter((id) => !ownedCompanyIds.has(id));

    // Compute levels bottom-up so that co-parents of a shared child end up on the
    // same row: leaves = 0, parent = max(child levels) + 1. We then flip the
    // numbering so roots sit at level 0 and leaves at maxLevel.
    const bottomLevels = new Map<number, number>();
    const computeBottomLevel = (id: number, visiting: Set<number>): number => {
      const cached = bottomLevels.get(id);
      if (cached !== undefined) return cached;
      if (visiting.has(id)) return 0; // cycle guard
      visiting.add(id);
      const childIds = (ownershipRefs.get(id) || []).filter((cid) => companyMap.has(cid));
      let lvl = 0;
      if (childIds.length > 0) {
        let maxChild = -1;
        childIds.forEach((cid) => {
          maxChild = Math.max(maxChild, computeBottomLevel(cid, visiting));
        });
        lvl = maxChild + 1;
      }
      visiting.delete(id);
      bottomLevels.set(id, lvl);
      return lvl;
    };
    allCompanies.forEach((c) => computeBottomLevel(c.modelDataId, new Set()));

    const maxBottom = Array.from(bottomLevels.values()).reduce((m, v) => Math.max(m, v), 0);

    allCompanies.forEach((company) => {
      const bl = bottomLevels.get(company.modelDataId) ?? 0;
      company.level = maxBottom - bl;
    });

    // Positionnement bottom-up : centrer chaque parent au-dessus de ses enfants
    const HORIZONTAL_SPACING = 250;
    const VERTICAL_SPACING = 180;
    const START_Y = 80;
    const CENTER_X = 400;

    const positioned = new Set<number>();
    let nextLeafSlot = 0;

    const positionSubtree = (nodeId: number): { minX: number; maxX: number } => {
      const company = companyMap.get(nodeId);
      if (!company) return { minX: 0, maxX: 0 };

      // Déjà positionné (noeud partagé) : retourner sa position actuelle
      if (positioned.has(nodeId)) {
        return { minX: company.x, maxX: company.x };
      }
      positioned.add(nodeId);

      const childIds = (ownershipRefs.get(nodeId) || []).filter((cid) => companyMap.has(cid));

      if (childIds.length === 0) {
        // Feuille : placer à la prochaine position libre
        company.x = nextLeafSlot * HORIZONTAL_SPACING;
        company.y = START_Y + company.level * VERTICAL_SPACING;
        nextLeafSlot++;
        return { minX: company.x, maxX: company.x };
      }

      // Positionner les enfants d'abord (récursif)
      let minX = Infinity;
      let maxX = -Infinity;
      childIds.forEach((childId) => {
        const bounds = positionSubtree(childId);
        minX = Math.min(minX, bounds.minX);
        maxX = Math.max(maxX, bounds.maxX);
      });

      // Centrer le parent au-dessus de ses enfants
      company.x = (minX + maxX) / 2;
      company.y = START_Y + company.level * VERTICAL_SPACING;

      return { minX, maxX };
    };

    // Positionner chaque arbre depuis ses racines
    rootCompanyIds.forEach((rootId) => {
      positionSubtree(rootId);
    });

    // Positionner les noeuds orphelins (non atteints par BFS)
    allCompanies.forEach((company) => {
      if (!positioned.has(company.modelDataId)) {
        company.x = nextLeafSlot * HORIZONTAL_SPACING;
        company.y = START_Y + company.level * VERTICAL_SPACING;
        nextLeafSlot++;
      }
    });

    // Resolve horizontal overlaps at each level. When several companies own the
    // same child, centering each of them above that child would stack them at
    // the same x. We spread siblings that collide by MIN_GAP pixels.
    const byLevel = new Map<number, CompanyBlock[]>();
    allCompanies.forEach((c) => {
      const list = byLevel.get(c.level) ?? [];
      list.push(c);
      byLevel.set(c.level, list);
    });
    Array.from(byLevel.values()).forEach((levelList) => {
      levelList.sort((a, b) => a.x - b.x);
      for (let i = 1; i < levelList.length; i++) {
        const minX = levelList[i - 1].x + HORIZONTAL_SPACING;
        if (levelList[i].x < minX) levelList[i].x = minX;
      }
    });

    // Recenter shared children: each owned company whose parents are now at
    // their final x position should sit at the midpoint between the leftmost
    // parent's left edge and the rightmost parent's right edge.
    const parentsOf = new Map<number, number[]>();
    ownershipRefs.forEach((ownedIds, ownerId) => {
      ownedIds.forEach((cid) => {
        const arr = parentsOf.get(cid) ?? [];
        arr.push(ownerId);
        parentsOf.set(cid, arr);
      });
    });
    const BLOCK_WIDTH = 192; // w-48
    const companiesTopDown = [...allCompanies].sort((a, b) => a.level - b.level);
    companiesTopDown.forEach((company) => {
      const parents = (parentsOf.get(company.modelDataId) ?? [])
        .map((pid) => companyMap.get(pid))
        .filter((p): p is CompanyBlock => p !== undefined);
      if (parents.length < 2) return;
      const minLeft = Math.min(...parents.map((p) => p.x));
      const maxRight = Math.max(...parents.map((p) => p.x + BLOCK_WIDTH));
      company.x = (minLeft + maxRight) / 2 - BLOCK_WIDTH / 2;
    });

    // Centrer l'ensemble autour de CENTER_X
    const xs = allCompanies.map((c) => c.x);
    const currentCenter = (Math.min(...xs) + Math.max(...xs)) / 2;
    const offset = CENTER_X - currentCenter;
    allCompanies.forEach((c) => {
      c.x += offset;
    });

    return allCompanies;
  };

  // Handler pour ajouter une nouvelle company (affichage optimiste)
  const handleAddCompany = async () => {
    if (!userRights.right_to_write) {
      alert('You do not have permission to create companies');
      return;
    }

    setIsLoading(true);

    try {
      const inputTree = currentState['Input'].tree;
      const rootInputItem = inputTree.items[inputTree.rootId];
      const parentId = rootInputItem?.id || 0;

      const response = await apiService.postModelData(
        'New Company',
        'company',
        parseInt(scenario_id),
        parentId,
        companies.length
      );

      if (response && response.ok) {
        const newItems = await response.json();
        const newModelDataEntries = Object.entries(newItems['input'] || {});

        if (newModelDataEntries.length > 0) {
          const [newId, newModelData]: any = newModelDataEntries[0];
          const companyId = `company-${newId}`;

          // Affichage optimiste : ajouter le bloc immédiatement sans attendre reloadState
          const newCompany: CompanyBlock = {
            id: companyId,
            modelDataId: parseInt(newId),
            modelData: newModelData || ({} as IModelData),
            name: newModelData?.name || 'New Company',
            type: 'Company',
            x: 0,
            y: 0,
            level: 0,
          };

          // Recalculer les positions avec la nouvelle company incluse
          const allCompanies = [...companies.map((c) => ({ ...c })), newCompany];
          const allModelDatas = [...structureInputs, newModelData].filter(Boolean);
          setCompanies(calculateHierarchyAndPosition(allCompanies, allModelDatas));
          setSelectedCompanyId(companyId);
          setEditingNameId(companyId);

          // Synchroniser l'état parent en arrière-plan
          currentState.isComputed = false;
          if (reloadState) {
            reloadState();
          }
        }
      } else if (response) {
        // Refused by the API (e.g. the plan's company cap): say why instead of silently resetting.
        const body = await response.json().catch(() => null);
        alert(body?.detail || 'Failed to create company');
      }
    } catch (error) {
      console.error('Error creating company:', error);
      alert('Failed to create company');
    } finally {
      setIsLoading(false);
    }
  };

  // Handler pour renommer une company
  const handleRenameCompany = async (companyId: string, newName: string) => {
    if (!userRights.right_to_write || !newName.trim()) {
      setEditingNameId(null);
      return;
    }

    const company = companies.find((c) => c.id === companyId);
    if (!company) return;

    setIsLoading(true);

    try {
      // Appeler l'API pour renommer le model data
      const response = await apiService.putModelData(
        company.modelDataId,
        newName.trim(),
        company.modelData.position
      );

      if (response && response.ok) {
        // Mettre à jour localement
        setCompanies(
          companies.map((c) =>
            c.id === companyId ? { ...c, name: newName.trim() } : c
          )
        );

        currentState.isComputed = false;

        if (reloadState) {
          reloadState();
        }
      }
    } catch (error) {
      console.error('Error renaming company:', error);
      alert('Failed to rename company');
    } finally {
      setIsLoading(false);
      setEditingNameId(null);
    }
  };

  // Handler pour supprimer une company.
  // IMPORTANT: we delegate to removeElemFromTree so the deletion is applied to
  // the shared TanStack tree cache (SCENARIO_TREE_KEY). Without that patch, the
  // block would reappear as soon as this component remounts (navigating back
  // into the section rebuilds `companies` from section.inputItems, which is
  // derived from the tree cache).
  const handleDeleteCompany = async (companyId: string) => {
    if (!userRights.right_to_write) {
      return;
    }

    const company = companies.find((c) => c.id === companyId);
    if (!company) return;

    setIsLoading(true);
    setDeleteConfirmId(null);

    // Snapshot state needed to roll back the optimistic update if the API fails.
    const companiesSnapshot = companies;
    const previousSelectedCompanyId = selectedCompanyId;

    // Optimistic local update so the block disappears immediately.
    deletedIdsRef.current.add(company.modelDataId);
    setCompanies(companies.filter((c) => c.id !== companyId));
    if (selectedCompanyId === companyId) {
      setSelectedCompanyId(null);
    }
    currentState.isComputed = false;

    // API delete + patch the TanStack tree cache. On failure, restore the UI.
    // `reloadState` is only fired on the legacy path; we rely on `onDone`
    // (fires on both paths) to clear the loading flag.
    removeElemFromTree(
      company.modelDataId.toString(),
      currentState,
      () => { if (reloadState) reloadState(); },
      undefined,
      patchTree,
      (error) => {
        console.error('Error deleting company:', error);
        deletedIdsRef.current.delete(company.modelDataId);
        setCompanies(companiesSnapshot);
        setSelectedCompanyId(previousSelectedCompanyId);
        setIsLoading(false);
        alert('Failed to delete company');
      },
      () => setIsLoading(false),
    );
  };

  // Handler pour sélectionner une company (clic)
  const handleCompanyClick = (companyId: string) => {
    setSelectedCompanyId(companyId);
  };

  // Handler pour le double-click (renommage)
  const handleDoubleClick = (companyId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!userRights.right_to_write) return;
    setEditingNameId(companyId);
  };

  // Handler pour le pan (déplacement de la vue)
  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      setPan({ x: pan.x + dx, y: pan.y + dy });
      setPanStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handlePanStart = (e: React.MouseEvent) => {
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
      e.preventDefault();
    }
  };

  // Handler pour le zoom
  const handleZoom = (delta: number) => {
    const newZoom = Math.max(0.5, Math.min(2, zoom + delta));
    setZoom(newZoom);
  };

  // Center the content horizontally + vertically in the workspace at the given zoom.
  // Uses the bounds of all displayed companies (left edge → left edge + block width).
  const BLOCK_WIDTH = 192; // w-48
  const BLOCK_HEIGHT = 60;
  const computeCenteredPan = useCallback(
    (zoomLevel: number): { x: number; y: number } | null => {
      if (!workspaceRef.current || displayCompanies.length === 0) return null;
      const rect = workspaceRef.current.getBoundingClientRect();
      const xs = displayCompanies.map((c) => c.x);
      const ys = displayCompanies.map((c) => c.y);
      const contentCenterX = (Math.min(...xs) + Math.max(...xs) + BLOCK_WIDTH) / 2;
      const contentCenterY = (Math.min(...ys) + Math.max(...ys) + BLOCK_HEIGHT) / 2;
      return {
        x: rect.width / 2 - contentCenterX * zoomLevel,
        y: rect.height / 2 - contentCenterY * zoomLevel,
      };
    },
    [displayCompanies],
  );

  // Auto-center once when companies first render (and workspace has a measured size).
  useEffect(() => {
    if (hasAutoCenteredRef.current) return;
    if (displayCompanies.length === 0) return;
    const centered = computeCenteredPan(zoom);
    if (centered) {
      setPan(centered);
      hasAutoCenteredRef.current = true;
    }
  }, [displayCompanies, zoom, computeCenteredPan]);

  // Wrapper memoized pour reloadState (évite que tous les FieldComponents re-render)
  const handleReloadState = useCallback(() => {
    if (reloadState) {
      reloadState();
    }
    // Les connexions sont recalculées automatiquement via useMemo
  }, [reloadState]);

  // Rendu des fields du model data sélectionné avec les composants Input ou Sensitivity
  const renderModelDataFields = () => {
    if (!selectedModelData || !selectedModelData.fields) {
      return (
        <div className="text-xs text-slate-400 italic">
          No company selected or no fields available
        </div>
      );
    }

    const fields = selectedModelData.fields;
    const fieldEntries = Object.entries(fields).filter(([_, field]: any) => !field.hide);

    if (fieldEntries.length === 0) {
      return (
        <div className="text-xs text-slate-400 italic">
          No fields available for this company
        </div>
      );
    }

    // En mode sensitivity, utiliser les composants Sensitivity
    if (isSensitivityMode && selectedTreeItem?.sensitivityModelData != null) {
      return (
        <div className="space-y-3">
          {fieldEntries.map(([fieldName, fieldData]) => {
            const field: any = fieldData;
            const SensitivityFieldComponent = SensitivityFieldByType[field.type];

            if (!SensitivityFieldComponent) return null;

            return (
              <div key={fieldName} className="border-b border-slate-100 pb-2 last:border-0">
                <span className="text-lg font-medium text-slate-900 mb-2 block">
                  {snakeToPascal(fieldName)}
                </span>
                <SensitivityFieldComponent
                  name={snakeToPascal(fieldName)}
                  item={selectedTreeItem}
                  field={field}
                  reloadState={handleReloadState}
                  currentState={currentState}
                  currentSensitivity={currentSensitivity}
                  recreateExcelInput={() => {}}
                  userRights={userRights}
                />
              </div>
            );
          })}
        </div>
      );
    }

    // Mode normal : composants Input
    return (
      <div className="space-y-3">
        {fieldEntries.map(([fieldName, fieldData]) => {
          const field: any = fieldData;
          const FieldComponent = FieldByType[field.type];

          if (!FieldComponent) {
            // Fallback pour types non supportés
            const currentValue = field.value?.value !== undefined ? field.value.value : field.value || '';
            return (
              <div key={fieldName}>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  {field.label || fieldName}
                </label>
                <input
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-medium focus:ring-primary focus:border-primary disabled:bg-slate-100 disabled:text-slate-400"
                  type="text"
                  defaultValue={currentValue}
                  disabled={!userRights.right_to_write}
                />
              </div>
            );
          }

          return (
            <div key={fieldName} className="border-b border-slate-100 pb-2 last:border-0">
              <FieldComponent
                name={snakeToPascal(fieldName)}
                field={field}
                siblingFields={fields}
                reloadState={handleReloadState}
                currentState={currentState}
                recreateExcelInput={() => {}}
                userRights={userRights}
              />
            </div>
          );
        })}
      </div>
    );
  };

  // Rendu des connexions SVG (structure chart avec angles droits)
  const renderConnections = () => {
    // Grouper les connexions par parent
    const connectionsByParent = new Map<string, Connection[]>();
    connections.forEach((conn) => {
      if (!connectionsByParent.has(conn.fromId)) {
        connectionsByParent.set(conn.fromId, []);
      }
      connectionsByParent.get(conn.fromId)!.push(conn);
    });

    // Map pour lookups O(1)
    const displayCompanyMap = new Map(displayCompanies.map((c) => [c.id, c]));

    const BLOCK_HALF_WIDTH = 96; // w-48 = 192px / 2
    const BLOCK_HEIGHT = 60;

    return (
      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
        {Array.from(connectionsByParent.entries()).map(([parentId, parentConnections]) => {
          const parentCompany = displayCompanyMap.get(parentId);
          if (!parentCompany) return null;

          const childCompanies = parentConnections
            .map((conn) => ({
              company: displayCompanyMap.get(conn.toId),
              ownership: conn.ownership,
            }))
            .filter((c) => c.company != null) as Array<{ company: CompanyBlock; ownership?: number }>;

          if (childCompanies.length === 0) return null;

          // Position du bas-centre du bloc parent
          const parentBottomX = parentCompany.x * zoom + pan.x + BLOCK_HALF_WIDTH * zoom;
          const parentBottomY = (parentCompany.y + BLOCK_HEIGHT) * zoom + pan.y;

          // Positions du haut-centre de chaque enfant
          const childPositions = childCompanies.map((c) => ({
            x: c.company.x * zoom + pan.x + BLOCK_HALF_WIDTH * zoom,
            y: c.company.y * zoom + pan.y,
            ownership: c.ownership,
          }));

          // Point intermédiaire Y (milieu entre le bas du parent et le haut des enfants)
          const minChildY = Math.min(...childPositions.map((p) => p.y));
          const midY = (parentBottomY + minChildY) / 2;

          // Bornes horizontales de la barre (inclure la position du parent)
          const allX = [parentBottomX, ...childPositions.map((p) => p.x)];
          const leftX = Math.min(...allX);
          const rightX = Math.max(...allX);

          // Pourcentages empilés verticalement à la colonne du parent,
          // juste sous son bloc. Chaque % est ainsi aligné sur le bloc qui détient.
          const ownershipLabels = childPositions
            .map((cp, idx) => ({ ownership: cp.ownership, idx }))
            .filter((x) => x.ownership !== undefined) as Array<{ ownership: number; idx: number }>;

          return (
            <g key={`group-${parentId}`}>
              {/* Ligne verticale du parent vers la barre horizontale */}
              <line
                x1={parentBottomX} y1={parentBottomY}
                x2={parentBottomX} y2={midY}
                stroke="#94a3b8" strokeWidth="2"
              />

              {/* Barre horizontale reliant tous les enfants */}
              {childPositions.length > 1 || childPositions[0].x !== parentBottomX ? (
                <line
                  x1={leftX} y1={midY}
                  x2={rightX} y2={midY}
                  stroke="#94a3b8" strokeWidth="2"
                />
              ) : null}

              {/* Lignes verticales de la barre vers chaque enfant */}
              {childPositions.map((childPos, i) => (
                <line
                  key={i}
                  x1={childPos.x} y1={midY}
                  x2={childPos.x} y2={childPos.y}
                  stroke="#94a3b8" strokeWidth="2"
                />
              ))}

              {/* Pourcentages de détention : empilés sous le bloc du détenteur,
                  à sa verticale (alignés sur la colonne du parent). Affichage
                  conditionné par le toggle du bandeau d'outils. */}
              {showOwnershipPercents && ownershipLabels.map((lbl, row) => (
                <text
                  key={`pct-${lbl.idx}`}
                  x={parentBottomX + 8}
                  y={parentBottomY + 20 + row * 14 * zoom}
                  fill="#64748b"
                  fontSize={11 * zoom}
                  fontWeight="bold"
                  textAnchor="start"
                  dominantBaseline="hanging"
                  className="pointer-events-none"
                >
                  {lbl.ownership}%
                </text>
              ))}
            </g>
          );
        })}
      </svg>
    );
  };

  return (
    <div className="bg-white flex flex-col h-full overflow-hidden">
      {/* Breadcrumb + Back Button */}
      <div className="px-8 py-4 border-b border-slate-100 bg-white sticky top-0 z-20">
        <div className="flex items-center gap-3 mb-2">
          <TailwindButton
            variant="ghost"
            size="sm"
            icon="arrow_back"
            onClick={onBack}
          >
            Back to Overview
          </TailwindButton>
          <span className="text-xs text-slate-400 flex items-center gap-1">
            or press
            <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-300 rounded text-[10px] font-mono">Esc</kbd>
          </span>
        </div>

        {/* Section Header */}
        <div className="flex items-center gap-4">
          <div className={cn(
            'size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center'
          )}>
            <span className="material-symbols-outlined text-[28px]">
              {section.config.icon}
            </span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold text-slate-900">
                {section.config.name}
              </h2>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {section.config.description}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 flex-1 overflow-y-auto min-h-0">
        <div className="flex items-center justify-end mb-6">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowOwnershipPercents((v) => !v)}
              title={showOwnershipPercents ? 'Hide ownership percentages' : 'Show ownership percentages'}
              aria-pressed={showOwnershipPercents}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 border rounded text-xs font-semibold transition-colors',
                showOwnershipPercents
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'bg-white border-slate-200 text-slate-500 hover:border-primary',
              )}
            >
              <span className="material-symbols-outlined text-sm">percent</span>
            </button>
            <button
              onClick={() => handleZoom(0.1)}
              className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded text-xs font-semibold hover:border-primary transition-colors"
            >
              <span className="material-symbols-outlined text-sm">zoom_in</span>
            </button>
            <button
              onClick={() => handleZoom(-0.1)}
              className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded text-xs font-semibold hover:border-primary transition-colors"
            >
              <span className="material-symbols-outlined text-sm">zoom_out</span>
            </button>
            <span className="text-xs text-slate-500 font-medium px-2">{Math.round(zoom * 100)}%</span>
            <button
              onClick={() => {
                const resetZoom = 0.85;
                setZoom(resetZoom);
                const centered = computeCenteredPan(resetZoom);
                setPan(centered ?? { x: 0, y: 0 });
              }}
              className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded text-xs font-semibold hover:border-primary transition-colors"
            >
              <span className="material-symbols-outlined text-sm">refresh</span>
            </button>
          </div>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-2 gap-6">
          {/* Left: Workspace */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl relative overflow-hidden flex flex-col h-[600px]">
            {!isSensitivityMode && (
              <div className="absolute top-4 left-4 flex gap-2 z-10">
                <button
                  onClick={handleAddCompany}
                  disabled={isLoading || !userRights.right_to_write || companyCreationLocked}
                  title={companyCreationLocked ? 'Upgrade your plan' : ''}
                  className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-xs font-bold shadow-lg shadow-primary/20 hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="material-symbols-outlined text-sm">add_box</span>
                  {isLoading ? 'Creating...' : 'Add Company'}
                </button>
              </div>
            )}

            {/* Workspace Canvas */}
            <div
              ref={workspaceRef}
              className="flex-1 relative cursor-grab active:cursor-grabbing"
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onMouseDown={handlePanStart}
            >
              {/* Connexions SVG */}
              {renderConnections()}

              {/* Companies */}
              <div
                style={{
                  transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                  transformOrigin: '0 0',
                }}
              >
                {displayCompanies.map((company) => {
                  const sensitivityModified = hasSensitivityModified(company.modelDataId);
                  return (
                  <div
                    key={company.id}
                    className={`absolute w-48 p-4 rounded-lg shadow-sm text-center cursor-pointer select-none transition-[border-color,box-shadow] duration-150 ${
                      selectedCompanyId === company.id
                        ? 'border-2 border-primary ring-4 ring-primary/5'
                        : sensitivityModified
                          ? 'border-2 border-amber-400 bg-amber-50'
                          : 'border border-slate-200 hover:border-primary bg-white'
                    } ${selectedCompanyId !== company.id && !sensitivityModified ? 'bg-white' : ''}`}
                    style={{
                      left: `${company.x}px`,
                      top: `${company.y}px`,
                    }}
                    onClick={() => handleCompanyClick(company.id)}
                    onDoubleClick={(e) => !isSensitivityMode && handleDoubleClick(company.id, e)}
                  >
                    {companyFlags.get(company.id) && (
                      <span className="absolute top-1.5 left-2 text-base leading-none">
                        {companyFlags.get(company.id)}
                      </span>
                    )}
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">{company.type}</p>

                    {editingNameId === company.id ? (
                      <input
                        type="text"
                        defaultValue={company.name}
                        autoFocus
                        onBlur={(e) => handleRenameCompany(company.id, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            (e.target as HTMLInputElement).blur();
                          } else if (e.key === 'Escape') {
                            setEditingNameId(null);
                          }
                        }}
                        className="w-full text-sm font-bold text-slate-900 bg-transparent border-none outline-none px-2 py-1 text-center"
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <p className="text-sm font-bold text-slate-900">{company.name}</p>
                    )}

                    {/* Bouton delete (croix noire sans fond) */}
                    {selectedCompanyId === company.id && userRights.right_to_write && !isSensitivityMode && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirmId(company.id);
                        }}
                        className="absolute top-1 right-1 text-slate-400 hover:text-slate-900 transition-colors p-1"
                        title="Delete company"
                      >
                        <span className="material-symbols-outlined text-base">close</span>
                      </button>
                    )}
                  </div>
                  );
                })}
              </div>

              {/* Message si aucune company */}
              {displayCompanies.length === 0 && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <span className="material-symbols-outlined text-6xl text-slate-300 mb-2">account_tree</span>
                    <p className="text-sm text-slate-400">Click "Add Company" to start building your structure</p>
                    <p className="text-xs text-slate-400 mt-2">Alt+Click to pan • Scroll to zoom</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Properties Panel */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col gap-4 overflow-y-auto h-[600px]">
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-widest">
                  {selectedCompany ? `${selectedCompany.name} Details` : 'Entity Details'}
                </h4>
                {selectedCompany && (
                  <button
                    onClick={() => setIsExpandedOpen(true)}
                    className="p-1 rounded hover:bg-slate-100 transition-colors"
                    title="Expand"
                  >
                    <span className="material-symbols-outlined text-[18px] text-slate-500">open_in_full</span>
                  </button>
                )}
              </div>

              {selectedCompany ? (
                renderModelDataFields()
              ) : (
                <div className="text-xs text-slate-400 italic">
                  Select a company to view and edit its details
                </div>
              )}
            </div>

            {/* Expanded Modal */}
            {selectedCompany && (
              <TailwindModal
                isOpen={isExpandedOpen}
                onClose={() => setIsExpandedOpen(false)}
                title={`${selectedCompany.name} Details`}
                size="full"
              >
                <div className="-mx-6 -my-4 p-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 140px)' }}>
                  {/* Company Type Badge */}
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-xs font-bold text-white px-2 py-1 rounded bg-primary">
                      {snakeToPascal(selectedCompany.type)}
                    </span>
                  </div>

                  {/* Fields */}
                  {renderModelDataFields()}
                </div>
              </TailwindModal>
            )}
          </div>
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Delete Company</h3>
            <p className="text-sm text-slate-600 mb-6">
              Are you sure you want to delete this company? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteCompany(deleteConfirmId)}
                disabled={isLoading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
